/**
 * Stack & Save — add the selected rows to the cart.
 *
 * Each selected row becomes its own ordinary line item: `items[i][id]` +
 * `items[i][quantity]` and nothing else. No `properties`, no bundle id, no
 * grouping key — so the cart treats them as normal products, exactly as it
 * would from a product page.
 *
 * Request shape follows the theme's existing multi-item add
 * (js/react/frequently-bought.js:135) including `sections` / `sections_url`, so
 * the cart drawer and icon bubble re-render from the same response. In Rebuy
 * mode `cart-drawer-shim.js` maps renderContents() onto Smart Cart.
 */

const CART_SECTIONS = 'cart-drawer,cart-icon-bubble';

/** Variant ids of the rows the customer actually has selected. */
export const selectedVariantIds = (root) =>
  Array.from(root.querySelectorAll('[data-stack-checkbox]'))
    .filter((checkbox) => checkbox.checked && !checkbox.disabled)
    .map((checkbox) => checkbox.value)
    .filter(Boolean);

/** One plain line item per id — deliberately no properties. */
export const buildCartPayload = (variantIds) => {
  const formData = new FormData();

  variantIds.forEach((id, index) => {
    formData.append(`items[${index}][id]`, id);
    formData.append(`items[${index}][quantity]`, 1);
  });

  const drawer = document.querySelector('cart-drawer');
  const sections = drawer?.getSectionsToRender
    ? drawer.getSectionsToRender().map((section) => section.id).join(',')
    : CART_SECTIONS;

  formData.append('sections', sections);
  formData.append('sections_url', window.location.pathname);

  return formData;
};

const showError = (root, message) => {
  const node = root.querySelector('[data-stack-error]');
  if (!node) return;
  node.textContent = message;
  node.hidden = !message;
};

const setBusy = (button, busy) => {
  button.classList.toggle('is-loading', busy);
  button.disabled = busy;
};

/** Hands the response to the drawer so the cart UI reflects the new lines. */
const renderCart = (data) => {
  const drawer = document.querySelector('cart-drawer');
  if (drawer?.renderContents) {
    drawer.renderContents(data);
    drawer.classList.remove('is-empty');
  }
};

/**
 * Posts the current selection. Resolves once the cart UI has been refreshed;
 * surfaces Shopify's own message (e.g. insufficient inventory) on failure.
 */
export default async function addSelectionToCart(root) {
  const button = root.querySelector('[data-stack-submit]');
  const variantIds = selectedVariantIds(root);

  if (!button || variantIds.length === 0) return;

  showError(root, '');
  setBusy(button, true);

  try {
    const url = window.routes?.cart_add_url || '/cart/add';
    const response = await fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: buildCartPayload(variantIds),
    });

    const data = await response.json();

    // Shopify answers 422 with a body carrying status/message/description.
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not add these products to your cart.');
    }

    renderCart(data);
  } catch (error) {
    console.error('[stack-and-save] add to cart failed', error);
    showError(root, error.message || 'Could not add these products to your cart.');
  } finally {
    setBusy(button, false);
  }
}
